/**
 * Experience Marker Component
 *
 * Creates DOM elements for experience markers on the map
 * Handles pin styling, colors, and price labels based on experience type
 */

import { MapMarker } from './types';

/**
 * Get color for location type based on Google Places API types
 * Returns a color that represents the location category
 */
export function getLocationTypeColor(types?: string[]): string {
  if (!types || types.length === 0) {
    return '#EA4335'; // Default red
  }

  const normalizedTypes = types.map(t => t.toLowerCase());

  // Priority-based type mapping (check in order of specificity)
  // Natural features and outdoor
  if (normalizedTypes.some(t => ['beach', 'natural_feature'].includes(t))) {
    return '#06B6D4'; // Cyan for beaches/natural features
  }
  if (normalizedTypes.some(t => ['mountain', 'hill'].includes(t))) {
    return '#8B5CF6'; // Purple for mountains
  }
  if (normalizedTypes.some(t => ['lake', 'river', 'waterfall'].includes(t))) {
    return '#3B82F6'; // Blue for water features
  }
  if (
    normalizedTypes.some(t => ['park', 'campground', 'rv_park'].includes(t))
  ) {
    return '#10B981'; // Green for parks
  }

  // Cultural and historical
  if (normalizedTypes.some(t => ['museum', 'art_gallery'].includes(t))) {
    return '#F59E0B'; // Amber for museums/galleries
  }
  if (normalizedTypes.some(t => ['historical_site', 'monument'].includes(t))) {
    return '#EF4444'; // Red for historical sites
  }
  if (
    normalizedTypes.some(t =>
      ['tourist_attraction', 'point_of_interest'].includes(t)
    )
  ) {
    return '#EC4899'; // Pink for tourist attractions
  }

  // Entertainment
  if (
    normalizedTypes.some(t => ['amusement_park', 'zoo', 'aquarium'].includes(t))
  ) {
    return '#F97316'; // Orange for entertainment
  }
  if (normalizedTypes.some(t => ['stadium', 'sports_complex'].includes(t))) {
    return '#DC2626'; // Dark red for sports venues
  }

  // Default
  return '#EA4335'; // Default red
}

/**
 * Get CSS variable name for category offset color based on experience type
 * Returns CSS variable with fallback color
 */
export function getExperienceBadgeOffsetColorCSS(
  experienceType?: string
): string {
  if (!experienceType) return '#EA4335'; // Default red for regular locations

  const type = experienceType.toLowerCase();

  // CSS variable mapping for experience types
  const cssVarMap: Record<string, string> = {
    signature: 'var(--Colors-Category-Signature-Offset, #e9d5ff)',
    package: 'var(--Colors-Category-Package-Offset, #a5f3fc)',
    packages: 'var(--Colors-Category-Package-Offset, #a5f3fc)',
    activity: 'var(--Colors-Category-Activity-Offset, #fed7aa)',
    activities: 'var(--Colors-Category-Activity-Offset, #fed7aa)',
    stay: 'var(--Colors-Category-Stay-Offset, #d9f99d)',
    stays: 'var(--Colors-Category-Stay-Offset, #d9f99d)',
    'riders-corner': 'var(--Colors-Category-Activity-Offset, #fed7aa)',
    experience: '#3B82F6', // Blue for general experiences
    experiences: '#3B82F6',
    tour: '#EC4899', // Pink for tours
    tours: '#EC4899',
    event: '#EF4444', // Red for events
    events: '#EF4444',
  };

  return cssVarMap[type] || '#EA4335'; // Default red if type not found
}

/**
 * Create a pin element for a marker
 * Returns an HTMLElement that can be used with AdvancedMarkerElement
 */
export function createExperienceMarkerElement(
  marker: MapMarker
): HTMLElement | null {
  // Only create elements on client side
  if (typeof window === 'undefined') {
    console.warn(
      '🗺️ ExperienceMarker - Cannot create marker element on server side'
    );
    return null;
  }

  // Get badge offset color CSS variable based on whether it's an experience and its type
  const badgeOffsetColorCSS = marker.isExperience
    ? getExperienceBadgeOffsetColorCSS(marker.experienceType)
    : '#EA4335'; // Red color for all location markers

  // Create container for marker label
  const container = document.createElement('div');
  container.style.position = 'relative';
  container.style.cursor = 'pointer';
  container.title = marker.title || '';

  // Create marker badge with label and price
  if (marker.isExperience) {
    const markerBadge = document.createElement('div');
    markerBadge.style.display = 'flex';
    markerBadge.style.padding =
      'var(--spacing-padding-margins-1, 4px) var(--spacing-padding-margins-2, 8px) var(--spacing-padding-margins-1, 4px) var(--spacing-padding-margins-1, 4px)';
    markerBadge.style.justifyContent = 'center';
    markerBadge.style.alignItems = 'center';
    markerBadge.style.gap = 'var(--spacing-padding-margins-1, 4px)';
    markerBadge.style.borderRadius = '8px var(--Corner-Small, 8px) 8px 8px';
    markerBadge.style.background = badgeOffsetColorCSS;
    markerBadge.style.color = 'var(--Colors-Typography-primary, #1C1917)';
    markerBadge.style.fontFamily = 'Gantari, sans-serif';
    markerBadge.style.fontSize = '10px';
    markerBadge.style.fontStyle = 'normal';
    markerBadge.style.fontWeight = '700';
    markerBadge.style.lineHeight = 'normal';
    markerBadge.style.whiteSpace = 'nowrap';

    // Add price only (no experience type label)
    if (marker.price) {
      const priceSpan = document.createElement('span');
      priceSpan.textContent = marker.price;
      markerBadge.appendChild(priceSpan);
      container.appendChild(markerBadge);
    }
    // If no price, don't show the marker badge at all
  } else {
    // For location markers, create a simple red marker
    const markerPin = document.createElement('div');
    markerPin.style.width = '24px';
    markerPin.style.height = '24px';
    markerPin.style.borderRadius = '50% 50% 50% 0';
    markerPin.style.transform = 'rotate(-45deg)';
    markerPin.style.backgroundColor = '#EA4335'; // Red color for all location markers
    markerPin.style.border = '2px solid #FFFFFF';
    markerPin.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';
    markerPin.style.position = 'relative';

    // Inner circle for better visibility
    const innerCircle = document.createElement('div');
    innerCircle.style.width = '10px';
    innerCircle.style.height = '10px';
    innerCircle.style.borderRadius = '50%';
    innerCircle.style.backgroundColor = '#FFFFFF';
    innerCircle.style.position = 'absolute';
    innerCircle.style.top = '50%';
    innerCircle.style.left = '50%';
    innerCircle.style.transform = 'translate(-50%, -50%) rotate(45deg)';
    innerCircle.style.opacity = '0.9';

    markerPin.appendChild(innerCircle);
    container.appendChild(markerPin);
  }

  return container;
}
